import { parseCookies } from 'nookies';
import React, { useEffect } from 'react';

import { COOKIE_NAME_DEALERSHIP_ID } from '@/constants/dealership/dealership.constants';
import { minDesktop } from '@/constants/vars.constants';
import { useBodyTypesFilter } from '@/hooks/filters/body-types-filter.hook';
import { useBrandsRangesFilter } from '@/hooks/filters/brands-ranges-filter.hook';
import { useColorsFilter } from '@/hooks/filters/colors-filter.hook';
import { useDoorsFilter } from '@/hooks/filters/doors-filter.hook';
import { useDrivetrainFilter } from '@/hooks/filters/drivetrain-filter.hook';
import { useFeaturesFilter } from '@/hooks/filters/features-filter.hook';
import { useFinanceFilter } from '@/hooks/filters/finance-filter.hook';
import { useFuelConsumptionFilter } from '@/hooks/filters/fuel-consumption.hook';
import { useFuelTypeFilter } from '@/hooks/filters/fuel-type-filter.hook';
import { useMileageFilter } from '@/hooks/filters/mileage-filter.hook';
import { usePriceFilter } from '@/hooks/filters/price-filter.hook';
import { useProvincesDealershipsFilter } from '@/hooks/filters/provinces-dealerships-filter.hook';
import { useTransmissionTypeFilter } from '@/hooks/filters/transmission-type-filter.hook';
import { useYearsFilterHook } from '@/hooks/filters/years-filter.hook';
import { useWindowSize } from '@/hooks/window-size.hook';

import { BodyFilters } from '../../filters/body-filter/body-filter';
import { BrandsRangesSimple } from '../../filters/brand-ranges-simple/brand-ranges-simple';
import { FeaturesFilter } from '../../filters/features-filter/features-filter';
import { Filter } from '../../filters/filter/filter';
import { FiltersWrapper } from '../../filters/filters-wrapper/filters-wrapper';
import { Locations } from '../../filters/locations/locations';
import { MainSearchOld } from '../../filters/main-search/main-search-old';
import { MoreFilters } from '../../filters/more-filters/more-filters';
import { PriceFinance } from '../../filters/price-finance/price-finance';
import { YearsMileage } from '../../filters/years-mileage/years-mileage';
import { SearchFiltersWrapper } from '../../search/search-filters-wrapper/search-filters-wrapper';

export const SearchFilters: React.FC<{
  pageType?: string;
  isDisplayBodyType?: boolean;
}> = ({ pageType, isDisplayBodyType }) => {
  const {
    priceFilter,
    setSelectedPriceFilter,
    setPriceFilter,
    resetPriceFilter,
  } = usePriceFilter();
  const { setSelectedFinanceFilter } = useFinanceFilter();
  const {
    yearsFilter,
    setSelectedYearsFilter,
    setYearsFilter,
    resetYearsFilter,
  } = useYearsFilterHook();
  const {
    mileageFilter,
    setSelectedMileageFilter,
    setMileageFilter,
    resetMileageFilter,
  } = useMileageFilter();
  const { provincesDealershipsFilter, setProvincesDealershipsFilter } =
    useProvincesDealershipsFilter();
  const {
    consumptionFilter,
    setSelectedConsumptionFilter,
    setConsumptionFilter,
    resetConsumptionFilter,
  } = useFuelConsumptionFilter();

  const { resetBrandsRanges } = useBrandsRangesFilter();
  const { resetSelectedBodyTypes } = useBodyTypesFilter();
  const { resetSelectedColorFilter } = useColorsFilter();
  const { resetSelectedDoorsFilter } = useDoorsFilter();
  const { resetSelectedTransmissionTypes } = useTransmissionTypeFilter();
  const { resetSelectedFuelTypes } = useFuelTypeFilter();
  const { resetSelectedDrivetrainTypes } = useDrivetrainFilter();
  const { resetSelectedFeatures } = useFeaturesFilter();

  const size = useWindowSize();

  const onClosePriceFilter = () => {
    setSelectedPriceFilter(priceFilter);
    setPriceFilter(priceFilter);
    setSelectedFinanceFilter(null);
  };

  const onCloseMoreFilter = () => {
    setConsumptionFilter(consumptionFilter);
    setSelectedConsumptionFilter(consumptionFilter);
    setYearsFilter(yearsFilter);
    setSelectedYearsFilter(yearsFilter);
    setMileageFilter(mileageFilter);
    setSelectedMileageFilter(mileageFilter);
  };

  const onCloseYearsAndMileageFilter = () => {
    setYearsFilter(yearsFilter);
    setSelectedYearsFilter(yearsFilter);
    setMileageFilter(mileageFilter);
    setSelectedMileageFilter(mileageFilter);
  };

  const onCloseLocationFilter = () => {
    setProvincesDealershipsFilter(provincesDealershipsFilter);
  };

  const [isKiaDealerShipActive, setIsKiaDealerShipActive] =
    React.useState(false);

  useEffect(() => {
    const cookies = parseCookies();

    const kiaDealarshipId = cookies?.[COOKIE_NAME_DEALERSHIP_ID];

    if (kiaDealarshipId) {
      setIsKiaDealerShipActive(true);
    }
  }, []);

  const handleSearch = () => {
    // reset all other filters
    resetPriceFilter();
    setSelectedFinanceFilter('');
    resetYearsFilter();
    resetMileageFilter();
    resetConsumptionFilter();

    resetBrandsRanges(null);
    resetSelectedBodyTypes(null);
    resetSelectedColorFilter(null);
    resetSelectedDoorsFilter(null);
    resetSelectedTransmissionTypes(null);
    resetSelectedFuelTypes(null);
    resetSelectedDrivetrainTypes(null);
    resetSelectedFeatures(null);
  };

  return (
    <SearchFiltersWrapper>
      <MainSearchOld onSearch={handleSearch} />
      <FiltersWrapper>
        <Filter
          position="bottomLeft"
          contentProps={true}
          content={<BrandsRangesSimple />}
        >
          {size?.width && size?.width < minDesktop
            ? 'Make/Model'
            : 'Make & Models'}
        </Filter>
        <Filter
          key={'Body'}
          contentProps={true}
          content={
            <BodyFilters
              pageType={pageType}
              isDisplayBodyType={isDisplayBodyType}
            />
          }
          position="bottom"
        >
          Body
        </Filter>
        {size?.width && size?.width < minDesktop ? (
          <Filter
            key={'Price'}
            onCloseMobileFilter={onClosePriceFilter}
            contentProps={true}
            content={<PriceFinance />}
          >
            Price
          </Filter>
        ) : (
          <Filter
            key={'Price/Finance'}
            position="bottomLeft"
            onCloseFilter={onClosePriceFilter}
            contentProps={true}
            content={<PriceFinance />}
          >
            Price / Finance
          </Filter>
        )}

        {size?.width && size?.width < minDesktop ? (
          <Filter
            key={'Mileage'}
            content={<YearsMileage />}
            onCloseFilter={onCloseYearsAndMileageFilter}
            position="bottomRight"
          >
            Mileage
          </Filter>
        ) : (
          <Filter
            key={'Years-Mileage'}
            content={<YearsMileage />}
            onCloseFilter={onCloseYearsAndMileageFilter}
            position="bottomRight"
          >
            Years & Mileage
          </Filter>
        )}

        <Filter
          key={'Features'}
          position="bottomRight"
          content={<FeaturesFilter />}
        >
          Features
        </Filter>
        {!isKiaDealerShipActive && (
          <Filter
            key={'Location'}
            position="bottomRight"
            content={<Locations />}
            contentProps={true}
            onCloseFilter={onCloseLocationFilter}
          >
            Location
          </Filter>
        )}
        <Filter
          key={'MoreFilters'}
          position="bottomRight"
          content={<MoreFilters />}
          onCloseFilter={onCloseMoreFilter}
        >
          More Filters
        </Filter>
      </FiltersWrapper>
    </SearchFiltersWrapper>
  );
};
